import type { Asset, Deal } from "@/types/deal";
import {
  formatMoney,
  formatMultiplier,
  formatPercent,
  formatYearBasedLines,
  formatLetteredList,
  getCurrencyDisplayName,
  getInterestBenchmark,
  getSponsorName,
  isBlank,
  isRefinancingDeal,
  joinBorrowerNames,
} from "./formatters";
import type { TermSheetSectionRow } from "./types";

function row(
  id: string,
  label: string,
  content: string,
  included = true,
): TermSheetSectionRow {
  return {
    id,
    label,
    content,
    included: included && content.trim().length > 0,
  };
}

/** Default to 1.5% only when no floor is set; a 0% floor is a real value. */
function formatRateFloor(rateFloor: number | null | undefined): string {
  if (rateFloor === null || rateFloor === undefined) return "1.5%";
  return `${rateFloor}%`;
}

function buildBorrowersSection(deal: Deal): TermSheetSectionRow {
  const names = joinBorrowerNames(
    deal.sponsor_borrowers,
    deal.secondary_sponsor_borrowers,
  );

  const content =
    names.length > 0
      ? `${names.map((name) => `[${name}]`).join(" and ")} incorporated in [●] with registration number [●].`
      : "[●] incorporated in [●] with registration number [●].";

  return row("borrowers", "Borrower(s)/Obligors", content);
}

export function buildAgentContent(deal: Deal): string {
  const facility = deal.facility_agent_name?.trim();
  const security = deal.security_agent_name?.trim();

  if (facility && security && facility !== security) {
    return `[${facility}] / [${security}]`;
  }
  if (facility) {
    return `[${facility}]`;
  }
  if (security) {
    return `[${security}]`;
  }

  return "TenFifty Capital Management Limited";
}

function buildAgentSection(deal: Deal): TermSheetSectionRow {
  return row(
    "agent",
    "Agent / Facility Agent and Security Agent",
    buildAgentContent(deal),
  );
}

export function buildClosingDisclaimer(deal: Deal): string {
  const agentName = buildAgentContent(deal);

  return `DISCLAIMER

This term sheet and the statements contained herein do not (and are not intended to) impose on TenFifty Capital, Apollo or any of its affiliates any legal obligation or duty whatsoever in relation to the Facilities or otherwise. TenFifty Capital or Apollo will not in any circumstances (whether in contract, tort or otherwise) be liable to any person if it does not arrange or underwrite the Facilities.

Notwithstanding the foregoing, in consideration of TenFifty Capital or Apollo providing this term sheet and progressing the preparation of a credit application in relation to the Facilities, by your signature below you agree that the provisions contained in the above sections of this term sheet entitled "Disclaimer", "Costs and Expenses", "Confidentiality", "Governing law" and "Jurisdiction" will be binding on you in accordance with the terms of those sections.

If you would like to proceed with the terms set out in this term sheet, please sign and return one copy of this term sheet by no later than [5:00 p.m.] London time on [●].

We accept the terms set out in this term sheet and acknowledge that the sections entitled "Disclaimer", "Costs and Expenses", "Confidentiality", "Governing law" and "Jurisdiction" are legally binding on us.

Accepted and agreed:

____________________________________

Signed on behalf of [include Sponsor entity's full legal name]

By: [include name of signatory]

Title: Authorised Signatory

Acknowledged by:

____________________________________

Signed on behalf of ${agentName}

By:

Title: Authorised Signatory`;
}

function buildPropertySection(deal: Deal, assets: Asset[]): TermSheetSectionRow {
  const currency = deal.loan_amount_currency;
  const blocks = assets
    .map((asset) => {
      const addressParts = [
        asset.address_1,
        asset.address_2,
        asset.city,
        asset.postcode,
      ]
        .map((part) => part?.trim())
        .filter(Boolean);
      const amount = asset.valuation_amount || asset.acquisition_price;
      const amountText = formatMoney(amount, currency);

      const lines = [
        asset.asset_name ? `[${asset.asset_name}]` : "",
        addressParts.length > 0 ? `[${addressParts.join(", ")}]` : "",
        amountText ? `[${amountText}]` : "",
      ].filter(Boolean);

      return lines.join("\n");
    })
    .filter(Boolean);

  const label =
    assets.length > 1 || deal.is_portfolio ? "Properties" : "Property";

  return row("property", label, blocks.join("\n\n"), blocks.length > 0);
}

function buildMaturitySection(deal: Deal): TermSheetSectionRow {
  const initial = deal.initial_loan_term;
  const full = deal.full_loan_term;

  let content = "As set out in the Facility Agreement.";

  if (initial === 2 && full === 3) {
    content = `The Interest Payment Date falling immediately after the date falling [${initial}] year(s) from the first utilisation date (the Initial Maturity Date) or the Extended Maturity Date (as defined below) if extended in accordance with the Extension Option below.`;
  } else if (!isBlank(initial)) {
    const years = full ?? initial;
    content = `Subject to the Extension Option below, the Maturity Date shall be the Interest Payment Date falling after [${years}] year(s) from the first utilisation date (to be finalised in the Facility Agreement).`;
  }

  return row("maturity", "Maturity Date", content);
}

function buildExtensionSection(deal: Deal): TermSheetSectionRow {
  const initial = deal.initial_loan_term;
  const full = deal.full_loan_term;

  const intro =
    !isBlank(initial) && !isBlank(full) && full !== initial
      ? `The Initial Maturity Date can be extended at the election of the Borrower to the Interest Payment Date falling immediately after the date falling [${full}] year(s) from the first utilisation date (the Extended Maturity Date) subject to:`
      : "Extension of the Maturity Date may be available at the election of the Borrower, subject to:";

  const conditions: string[] = [];

  if (!isBlank(deal.extension_condition_dy)) {
    conditions.push(
      `Debt Yield being greater than or equal to [${formatPercent(deal.extension_condition_dy)}] (the Extension Debt Yield)`,
    );
  }
  if (!isBlank(deal.extension_condition_ltv)) {
    conditions.push(
      `Loan to Value (based on a valuation dated no more than 3 months prior to the applicable maturity date) not being greater than [${formatPercent(deal.extension_condition_ltv)}] (the Extension LTV)`,
    );
  }
  if (!isBlank(deal.extension_condition_ltc)) {
    conditions.push(
      `Loan to Cost not being greater than [${formatPercent(deal.extension_condition_ltc)}] (the Extension LTC)`,
    );
  }
  if (!isBlank(deal.extension_condition_icr)) {
    conditions.push(
      `ICR being greater than or equal to [${formatMultiplier(deal.extension_condition_icr)}] (the Extension ICR)`,
    );
  }
  if (!isBlank(deal.extension_condition_dscr)) {
    conditions.push(
      `DSCR being greater than or equal to [${formatMultiplier(deal.extension_condition_dscr)}] (the Extension DSCR)`,
    );
  }

  conditions.push(
    "interest rate cap in respect of the extension period which is satisfactory to the Lenders",
  );
  conditions.push(
    "no Default (i) is continuing on the date of the extension request (which shall be submitted no later than [1] month prior to the Initial Maturity Date and no earlier than [3] months prior to the Initial Maturity Date), (ii) is continuing on the Initial Maturity Date or (iii) would result from the extension",
  );

  if (!isBlank(deal.extension_fee)) {
    conditions.push(
      `payment of an extension fee of [${formatPercent(deal.extension_fee)}] of the drawn amount of the Facility as at the Initial Maturity Date`,
    );
  }

  if (!isBlank(deal.extension_condition_occupancy)) {
    conditions.push(
      `Minimum occupancy of [${formatPercent(deal.extension_condition_occupancy)}]`,
    );
  }

  const lettered = formatLetteredList(conditions);

  let content = `${intro}\n\n${lettered}`;

  if (
    !isBlank(deal.extension_condition_dy) ||
    !isBlank(deal.extension_condition_ltv) ||
    !isBlank(deal.extension_condition_icr)
  ) {
    content +=
      "\n\nFor the avoidance of doubt, the Borrower may make a voluntary prepayment in the amount calculated by the Facility Agent as being required to achieve compliance with the Extension Debt Yield, Extension LTV or the Extension ICR (as applicable) in order to satisfy the relevant extension condition.";
  }

  return row("extension", "Extension Option", content);
}

function hasCapexFacility(deal: Deal): boolean {
  return [
    deal.capex_fac_loan_amount_day_one,
    deal.capex_fac_loan_amount_fully_funded,
    deal.capex_fac_ltc,
    deal.capex_fac_pt_capex_cost,
  ].some((value) => !isBlank(value));
}

function buildTotalCommitmentsSection(deal: Deal): TermSheetSectionRow {
  const currency = deal.loan_amount_currency;
  const facilityLabel = isRefinancingDeal(deal)
    ? "Refinancing Facility"
    : "Acquisition Facility";

  let intro =
    "The maximum Total Commitments under the Facilities will not exceed the lower of:";
  if (hasCapexFacility(deal)) {
    intro = `The sum of the ${facilityLabel} and the Capex Facility. In aggregate, the maximum Total Commitments under the Facilities will not exceed the lower of:`;
  }

  const bullets: string[] = [];
  const amount = deal.loan_amount_day_one ?? deal.loan_amount_fully_funded;
  if (!isBlank(amount)) bullets.push(`[${formatMoney(amount, currency)}]`);
  if (!isBlank(deal.ltv)) {
    bullets.push(`[${formatPercent(deal.ltv)}] Loan to Value`);
  }
  if (!isBlank(deal.ltc)) {
    bullets.push(`[${formatPercent(deal.ltc)}] Loan to Cost`);
  }
  if (!isBlank(deal.dy)) {
    bullets.push(`[${formatPercent(deal.dy)}] Debt Yield`);
  }

  let content =
    bullets.length > 0
      ? `${intro}\n\n${formatLetteredList(bullets)}`
      : "As set out in the Facility Agreement.";

  if (bullets.length > 0 && !isBlank(deal.ltc)) {
    content +=
      "\n\nLTC to be tested as a condition precedent to each utilisation of the Facilities.";
  }

  if (
    !isBlank(deal.loan_amount_day_one) &&
    !isBlank(deal.loan_amount_fully_funded) &&
    deal.loan_amount_day_one !== deal.loan_amount_fully_funded
  ) {
    const accordion =
      (deal.loan_amount_fully_funded ?? 0) - (deal.loan_amount_day_one ?? 0);
    content += `\n\nUncommitted Accordion: Up to [${formatMoney(accordion, currency)}] to support further investment.`;
  }

  return row("totalCommitments", "Total Commitments", content);
}

function buildAcquisitionFacilitySection(deal: Deal): TermSheetSectionRow {
  const hasValues = [
    deal.acq_fac_loan_amount_day_one,
    deal.acq_fac_loan_amount_fully_funded,
    deal.acq_fac_ltpp,
    deal.acq_fac_dy,
    deal.acq_fac_icr,
    deal.acq_fac_dscr,
  ].some((value) => !isBlank(value));

  if (!hasValues) {
    return row("acquisitionFacility", "Acquisition Facility", "", false);
  }

  const label = isRefinancingDeal(deal)
    ? "Refinancing Facility"
    : "Acquisition Facility";
  const currency = deal.loan_amount_currency;
  const bullets: string[] = [];

  if (!isBlank(deal.acq_fac_loan_amount_day_one)) {
    bullets.push(
      `[${formatMoney(deal.acq_fac_loan_amount_day_one, currency)}]`,
    );
  }
  if (!isBlank(deal.acq_fac_ltpp)) {
    bullets.push(
      `[${formatPercent(deal.acq_fac_ltpp)}] Loan to Purchase Price`,
    );
  } else if (!isBlank(deal.ltv)) {
    bullets.push(`[${formatPercent(deal.ltv)}] Loan to Value`);
  } else if (!isBlank(deal.ltc)) {
    bullets.push(`[${formatPercent(deal.ltc)}] Loan to Cost`);
  }
  if (!isBlank(deal.acq_fac_dy ?? deal.dy)) {
    bullets.push(`[${formatPercent(deal.acq_fac_dy ?? deal.dy)}] Debt Yield`);
  }

  const content = `The lower of:\n\n${formatLetteredList(bullets)}`;
  return row("acquisitionFacility", label, content);
}

function buildCapexFacilitySection(deal: Deal): TermSheetSectionRow {
  const hasValues = [
    deal.capex_fac_loan_amount_day_one,
    deal.capex_fac_ltc,
    deal.capex_fac_pt_capex_cost,
  ].some((value) => !isBlank(value));

  if (!hasValues) {
    return row("capexFacility", "Capex Facility", "", false);
  }

  const currency = deal.loan_amount_currency;
  const bullets: string[] = [];

  if (!isBlank(deal.capex_fac_loan_amount_day_one)) {
    bullets.push(
      `[${formatMoney(deal.capex_fac_loan_amount_day_one, currency)}]`,
    );
  }
  if (!isBlank(deal.capex_fac_ltc)) {
    bullets.push(`[${formatPercent(deal.capex_fac_ltc)}] Loan to Cost`);
  }
  if (!isBlank(deal.capex_fac_pt_capex_cost)) {
    bullets.push(
      `[${formatPercent(deal.capex_fac_pt_capex_cost)}] of Budgeted Costs`,
    );
  }

  const content = `The lower of:\n\n${formatLetteredList(bullets)}`;
  return row("capexFacility", "Capex Facility", content);
}

function buildInterestReserveSection(deal: Deal): TermSheetSectionRow {
  const hasValues =
    !isBlank(deal.int_res_fac_loan_amount_day_one) ||
    !isBlank(deal.int_res_fac_pt_cap_int_commitfees);

  if (!hasValues) {
    return row("interestReserve", "Interest Reserve Facility", "", false);
  }

  const currency = deal.loan_amount_currency;
  const bullets: string[] = [];

  if (!isBlank(deal.int_res_fac_loan_amount_day_one)) {
    bullets.push(
      `[${formatMoney(deal.int_res_fac_loan_amount_day_one, currency)}]`,
    );
  }
  if (!isBlank(deal.int_res_fac_pt_cap_int_commitfees)) {
    bullets.push(
      `[${formatPercent(deal.int_res_fac_pt_cap_int_commitfees)}] of projected interest expense and commitment fees up to the Initial Maturity Date`,
    );
  }

  const content = `The lower of:\n\n${formatLetteredList(bullets)}`;
  return row("interestReserve", "Interest Reserve Facility", content);
}

function buildFinancialCovenantsSection(deal: Deal): TermSheetSectionRow {
  const parts: string[] = [];

  const ltv = formatYearBasedLines(deal.covenant_ltv_years, deal.covenant_ltv);
  if (ltv) {
    parts.push(
      `Loan to Value: At any time, Loan to Value shall not be greater than:\n${ltv}`,
    );
  }

  const ltc = formatYearBasedLines(deal.covenant_ltc_years, deal.covenant_ltc);
  if (ltc) {
    parts.push(
      `Loan to Cost: At any time, Loan to Cost shall not be greater than:\n${ltc}`,
    );
  }

  const icr = formatYearBasedLines(
    deal.covenant_icr_years,
    deal.covenant_icr,
    "x",
  );
  if (icr) {
    parts.push(`ICR: On any test date, ICR shall be at least:\n${icr}`);
  }

  const dscr = formatYearBasedLines(
    deal.covenant_dscr_years,
    deal.covenant_dscr,
    "x",
  );
  if (dscr) {
    parts.push(`DSCR: On any test date, DSCR shall be at least:\n${dscr}`);
  }

  const dy = formatYearBasedLines(deal.covenant_dy_years, deal.covenant_dy);
  if (dy) {
    parts.push(
      `Debt Yield: On any Interest Payment Date the Debt Yield shall not be less than:\n${dy}`,
    );
  }

  return row("financialCovenants", "Financial Covenants", parts.join("\n\n"));
}

function buildCashTrapSection(deal: Deal): TermSheetSectionRow {
  const blocks: string[] = [
    "A Cash Trap Event will be continuing from the date of the Facilities Agreement until the Target Cash Trap Debt Yield and the Target Cash Trap Loan to Value is achieved for two consecutive Interest Payment Dates (the Initial Cash Trap End Date).",
  ];

  const dy = formatYearBasedLines(deal.cashtrap_dy_years, deal.cashtrap_dy);
  if (dy) {
    blocks.push(`Target Cash Trap Debt Yield:\n${dy}`);
  }

  const ltv = formatYearBasedLines(deal.cashtrap_ltv_years, deal.cashtrap_ltv);
  if (ltv) {
    blocks.push(`Target Cash Trap Loan to Value:\n${ltv}`);
  }

  const ltc = formatYearBasedLines(deal.cashtrap_ltc_years, deal.cashtrap_ltc);
  if (ltc) {
    blocks.push(`Target Cash Trap Loan to Cost:\n${ltc}`);
  }

  if (!isBlank(deal.cashtrap_icr)) {
    blocks.push(
      `Target Cash Trap ICR: [${formatMultiplier(deal.cashtrap_icr)}].`,
    );
  }
  if (!isBlank(deal.cashtrap_dscr)) {
    blocks.push(
      `Target Cash Trap DSCR: [${formatMultiplier(deal.cashtrap_dscr)}].`,
    );
  }

  blocks.push("Cash to be trapped in a lender controlled blocked account.");
  blocks.push(
    "Provided that no default is continuing, release mechanics from the Cash Trap Account may be agreed in the Facility Agreement (including application towards debt service, opex or capex in line with the business plan).",
  );
  blocks.push(
    "For the avoidance of doubt, if, following the end of the initial cash trap period, the agreed cash trap targets deteriorate, a Cash Trap Event shall occur again and customary provisions regarding release to the general account / prepayment will apply.",
  );

  return row("cashTrap", "Cash Trap Event", blocks.join("\n\n"));
}

function buildCommitmentFeeSection(deal: Deal): TermSheetSectionRow {
  if (isBlank(deal.commitment_fee)) {
    return row("commitmentFee", "Commitment Fee", "", false);
  }

  const margin = deal.margin ?? 0;
  const fee = deal.commitment_fee ?? 0;
  const isMarginBased = margin > 0 && Math.abs(fee - margin * 0.4) < 0.05;

  const content = isMarginBased
    ? `[40%] of the applicable Margin.`
    : `[${formatPercent(deal.commitment_fee)}] of the Total Commitments.`;

  return row("commitmentFee", "Commitment Fee", content);
}

const STATIC_SECTIONS: Array<{ id: string; label: string; content: string }> = [
  {
    id: "groupStructure",
    label: "Group structure",
    content: "[Please provide a group structure chart.]",
  },
  {
    id: "lenders",
    label: "Lenders",
    content:
      "Certain entities, funds and accounts managed by Apollo Management International LLP (AMI, together with its affiliated investment management entities, Apollo), and such other investment funds, separate accounts or vehicles managed or sub-advised by Apollo or accounts, funds, banks and financial institutions as may be owned or operated by Apollo.",
  },
  {
    id: "sustainability",
    label: "Sustainability",
    content:
      "TenFifty would like to offer a sustainability-linked transaction provision should that be of interest to the Borrower(s). The structure of the sustainability-linked transaction provision is to be defined and agreed with the borrower with the consent of the Majority Lenders in respect of the relevant Facilities.",
  },
  {
    id: "availabilityPeriod",
    label: "Availability Period",
    content:
      "Refinancing Facility: the facility shall be available in one single utilisation as set out in the Facility Agreement.\nCapex Facility: the facility shall be available in quarterly utilisations up to a date prior to the Initial Maturity Date.\nInterest Reserve Facility: the facility shall be available in quarterly utilisations until the Initial Maturity Date.",
  },
  {
    id: "interestPaymentDates",
    label: "Interest Payment Dates & reporting",
    content:
      "15 January, 15 April, 15 July and 15 October in each year and the Maturity Date, with the first Interest Payment Date to be agreed with the Borrower.\n\nReporting and compliance certificates to be provided 5 business days prior to each Interest Payment Date on the basis of reporting templates agreed as conditions precedent to initial utilisation.",
  },
  {
    id: "propertyProtectionLoans",
    label: "Property Protection Loans",
    content:
      "Customary for facilities of this type. The rate of interest on any Property Protection Loans will be the Default Interest rate.",
  },
  {
    id: "repayment",
    label: "Repayment",
    content:
      "The Borrower must repay the Facilities in full on the Maturity Date.",
  },
  {
    id: "prepayment",
    label: "Prepayment",
    content:
      "Customary for facilities of this nature including, without limitation, customary mandatory prepayment events and change of control.",
  },
  {
    id: "debtYieldCalculation",
    label: "Debt Yield Calculation",
    content:
      "For the purposes of calculating Debt Yield, adjustments (if any) to Net Operating Income will be as set out in the Facility Agreement and agreed reporting templates.",
  },
  {
    id: "hedging",
    label: "Hedging",
    content:
      "Hedging to be in the form of an interest rate cap for a term ending on the Maturity Date (strike and other terms to be agreed). The aggregate notional amount must at all times be no less than [100%] of the outstanding loans.",
  },
  {
    id: "defaultInterest",
    label: "Default Interest",
    content:
      "The prevailing rate of interest plus [2.00%] p.a. on the outstanding amount of the loans whilst an Event of Default is continuing.",
  },
  {
    id: "assetManagement",
    label: "Asset/Property Management Provisions",
    content: "Customary for facilities of this type.",
  },
  {
    id: "representations",
    label: "Representations / Undertakings / Events of Default / Security CPs",
    content:
      "Representations, undertakings, events of default and security conditions precedent: customary for facilities of this type.",
  },
  {
    id: "transfers",
    label: "Transfers",
    content:
      "No Obligor may transfer any of its rights or obligations under the Finance Documents without the prior consent of all of the Lenders.\nA Lender may assign, transfer, novate or dispose of any of its rights and/or obligations under any Finance Documents.\nNo restrictions on repo or total return swap funding arrangements.",
  },
  {
    id: "costsAndExpenses",
    label: "Costs and Expenses",
    content:
      "All legal, valuation and other third-party professionals' fees and out-of-pocket expenses (including any reasonable costs and expenses in respect of the transaction) (and applicable VAT) incurred in connection with the Facilities will be for the account of the Obligors and the Sponsor (whether or not the Facilities Agreement is signed).",
  },
  {
    id: "confidentiality",
    label: "Confidentiality",
    content:
      "This term sheet is confidential and none of the Sponsor, the Transaction Obligors or the Lenders (or any of their affiliates or professional advisors) may disclose any part of it to any person (other than its affiliates and professional advisors). No public announcements may be made in connection with this term sheet without the prior written consent of the Arranger and the Sponsor.",
  },
  {
    id: "documentation",
    label: "Documentation",
    content:
      "The Facilities Agreement will be based on the LMA recommended form of facilities agreement for investment real estate finance transactions as amended to incorporate the terms set out in this term sheet, the due diligence findings, and to include market standard terms as to (i) increased costs, (ii) illegality, (iii) FATCA, (iv) market disruption, (v) sanctions, (vi) indemnities in favour of the Finance Parties, (vii) disenfranchisement of transaction obligors and the Sponsor in relation to debt purchase transactions and (viii) limited recourse and non-petition language in respect of any Lender which is a compartment of a securitisation vehicle.",
  },
  {
    id: "governingLaw",
    label: "Governing Law",
    content: "English law (except for local security documents (as applicable)).",
  },
  {
    id: "jurisdiction",
    label: "Jurisdiction",
    content:
      "Courts of England and Wales save where inappropriate for security documents.",
  },
  {
    id: "tenfiftyProcessingFee",
    label: "TenFifty Processing Fee",
    content:
      "In consideration of TenFifty providing this term sheet and agreeing to progress a credit application, the Sponsor agrees that it shall pay to TenFifty a non-refundable processing fee of [€25,000].",
  },
];

export function buildTermSheetSections(
  deal: Deal,
  assets: Asset[],
): TermSheetSectionRow[] {
  const currency = deal.loan_amount_currency;
  const sponsorName = getSponsorName(deal);

  const dynamicSections: TermSheetSectionRow[] = [
    buildBorrowersSection(deal),
    row(
      "sponsor",
      "Sponsor",
      sponsorName ? `[${sponsorName}]` : "",
      !isBlank(sponsorName),
    ),
    buildAgentSection(deal),
    buildPropertySection(deal, assets),
    row(
      "currency",
      "Currency",
      getCurrencyDisplayName(currency)
        ? `[${getCurrencyDisplayName(currency)}]`
        : "",
      !isBlank(currency),
    ),
    buildMaturitySection(deal),
    buildExtensionSection(deal),
    buildTotalCommitmentsSection(deal),
    buildAcquisitionFacilitySection(deal),
    buildCapexFacilitySection(deal),
    buildInterestReserveSection(deal),
    row(
      "purpose",
      "Purpose",
      deal.deal_purpose ? `[${deal.deal_purpose}]` : "",
      !isBlank(deal.deal_purpose),
    ),
    row(
      "equityContribution",
      "Equity Contribution",
      !isBlank(deal.equity_contribution)
        ? `As a condition precedent to the first utilisation of the Facility, the Sponsor will make an initial equity contribution of [${formatMoney(deal.equity_contribution, currency)}] (application as agreed in the Facility Agreement).`
        : "As agreed in the Facility Agreement.",
      true,
    ),
    row(
      "margin",
      "Margin",
      !isBlank(deal.margin) ? `[${formatPercent(deal.margin)}] p.a.` : "",
      !isBlank(deal.margin),
    ),
    row(
      "interestRate",
      "Interest Rate",
      `Interest will accrue on the Facilities at a rate equal to the sum of:\n\n(a)\tthe Margin; plus\n(b)\t[${getInterestBenchmark(currency)}] (floored at [${formatRateFloor(deal.rate_floor)}])\n\nInterest under the Facilities will accrue on the basis of quarterly interest periods on an actual/360 basis.`,
      true,
    ),
    row(
      "amortisation",
      "Amortisation",
      deal.amortisation && !isBlank(deal.amortisation_amount)
        ? `[${formatMoney(deal.amortisation_amount, currency)}]`
        : "None required.",
      true,
    ),
    row(
      "makeWholeFee",
      "Make Whole Fee",
      !isBlank(deal.make_whole_fee_interestmonths)
        ? `During the period from the date of the Facilities Agreement and expiring on the date falling [${deal.make_whole_fee_interestmonths}] months after the first utilisation date (the Make Whole Fee Period), a make whole fee is payable on any repayment or prepayment of a loan and any cancellation of a commitment (including upon acceleration of the loans), in an amount equal to the amount of interest which would have accrued on the amount repaid, prepaid or cancelled (had such cancelled amount been a loan) from (and including) the date of such repayment, prepayment or cancellation to (and including) the expiry of the Make Whole Fee Period.`
        : "",
      !isBlank(deal.make_whole_fee_interestmonths),
    ),
    row(
      "arrangementFee",
      "Arrangement Fee",
      !isBlank(deal.arrangement_fee)
        ? `[${formatPercent(deal.arrangement_fee)}] of the Total Commitments.`
        : "",
      !isBlank(deal.arrangement_fee),
    ),
    buildCommitmentFeeSection(deal),
    row(
      "exitFee",
      "Exit Fee",
      !isBlank(deal.exit_fee)
        ? `[${formatPercent(deal.exit_fee)}] of the Total Commitments payable on the earlier of: (i) the Maturity Date; and (ii) the date on which the loans are repaid, prepaid or cancelled in full or the loans are accelerated.`
        : "",
      !isBlank(deal.exit_fee),
    ),
    row(
      "agencyFee",
      "Agency and Security Agency Fee",
      !isBlank(deal.facility_agent_fee)
        ? `[${formatMoney(deal.facility_agent_fee, currency)}] p.a. payable to the Facility Agent and the Security Agent in advance in equal quarterly instalments (save for the first instalment which will be a pro rata amount) on the first utilisation date and each Interest Payment Date.`
        : "",
      !isBlank(deal.facility_agent_fee),
    ),
    buildFinancialCovenantsSection(deal),
    buildCashTrapSection(deal),
  ];

  const staticRows = STATIC_SECTIONS.map((section) =>
    row(section.id, section.label, section.content, true),
  );

  return [
    dynamicSections[0],
    staticRows[0],
    dynamicSections[1],
    staticRows[1],
    ...dynamicSections.slice(2, 6),
    staticRows[2],
    ...dynamicSections.slice(6, 16),
    staticRows[3],
    staticRows[4],
    dynamicSections[16],
    staticRows[5],
    staticRows[6],
    staticRows[7],
    ...dynamicSections.slice(17, 24),
    staticRows[8],
    staticRows[9],
    staticRows[10],
    staticRows[11],
    staticRows[12],
    staticRows[13],
    staticRows[14],
    staticRows[15],
    staticRows[16],
    staticRows[17],
    staticRows[18],
    staticRows[19],
  ];
}

export function buildTermSheetPreview(
  header: string,
  sections: TermSheetSectionRow[],
): string {
  const body = sections
    .filter((section) => section.included && section.content.trim())
    .map((section) => `${section.label}\n${section.content}`)
    .join("\n\n");

  return `${header}\n\n${body}`;
}

export function buildTermSheetHeaderParts(deal: Deal): {
  title: string;
  date: string;
} {
  const date = new Date().toLocaleString("en-GB", {
    month: "long",
    year: "numeric",
  });

  return {
    title: `Indicative Senior Loan Terms – ${deal.deal_name ?? "Deal"}`,
    date,
  };
}

export function buildTermSheetHeader(deal: Deal): string {
  const { title, date } = buildTermSheetHeaderParts(deal);
  return `${title} ${date}`;
}
